import type { TimedEvent } from '@game-libs/timed-events-orchestrator';
import { TimedEventsOrchestrator } from '@game-libs/timed-events-orchestrator';
import { createGameStore, type PersistedGameStore } from '@game-libs/generic-game-state';
import { registerStoreReset } from './index';

// ---------------------------------------------------------------------------
// Timed events list (orchestrator owns runtime mutation; this store is the
// React/persist snapshot updated each tick and after dynamic addEvents).
// ---------------------------------------------------------------------------

export type TimedEventsState = {
  events: TimedEvent[];
};

export type TimedEventsActions = {
  setEvents: (events: TimedEvent[]) => void;
  /** Copy current orchestrator list into the store (call after addEvents/loadEvents outside the tick). */
  syncFromOrchestrator: () => void;
  sync: (patch: Partial<TimedEventsState>) => void;
  reset: () => void;
};

export type TimedEventsStore = TimedEventsState & TimedEventsActions;

const INITIAL_TIMED_EVENTS_STATE: TimedEventsState = {
  events: [],
};

export const useTimedEventsStore: PersistedGameStore<TimedEventsStore> = createGameStore<TimedEventsStore>(
  { saveKey: 'timed-events' },
  (set) => ({
    ...INITIAL_TIMED_EVENTS_STATE,

    setEvents: (events) => set({ events }),

    syncFromOrchestrator: () => {
      const orchestrator = TimedEventsOrchestrator();
      set({ events: [...orchestrator.allEvents] });
    },

    sync: (patch) => set(patch as TimedEventsStore),

    reset: () => set(INITIAL_TIMED_EVENTS_STATE),
  }),
);

registerStoreReset(() => useTimedEventsStore.getState().reset());
